/**
 * Zoho's 14 date-range presets, resolved client-side.
 *
 * The OAuth report endpoints require from_date/to_date even for presets, so we
 * compute the same window Zoho's metadata does: weeks run Sunday–Saturday,
 * the year is the Indian financial year (April–March).
 */

export type DatePresetId =
  | "Today"
  | "ThisWeek"
  | "ThisMonth"
  | "MonthToDate"
  | "ThisQuarter"
  | "QuarterToDate"
  | "ThisYear"
  | "YearToDate"
  | "PreviousDay"
  | "PreviousWeek"
  | "PreviousMonth"
  | "PreviousQuarter"
  | "PreviousYear"
  | "CustomDate";

export interface DatePreset {
  id: DatePresetId;
  label: string;
}

export const DATE_PRESETS: DatePreset[] = [
  { id: "Today", label: "Today" },
  { id: "ThisWeek", label: "This Week" },
  { id: "ThisMonth", label: "This Month" },
  { id: "MonthToDate", label: "Month To Date" },
  { id: "ThisQuarter", label: "This Quarter" },
  { id: "QuarterToDate", label: "Quarter To Date" },
  { id: "ThisYear", label: "This Year" },
  { id: "YearToDate", label: "Year To Date" },
  { id: "PreviousDay", label: "Previous Day" },
  { id: "PreviousWeek", label: "Previous Week" },
  { id: "PreviousMonth", label: "Previous Month" },
  { id: "PreviousQuarter", label: "Previous Quarter" },
  { id: "PreviousYear", label: "Previous Year" },
  { id: "CustomDate", label: "Custom" },
];

export interface DateRange {
  from: string;
  to: string;
}

const FY_START_MONTH = 3; // April (0-based)

const pad = (n: number) => String(n).padStart(2, "0");
export const toYmd = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const date = (y: number, m: number, d: number) => new Date(y, m, d);
const monthEnd = (y: number, m: number) => date(y, m + 1, 0);

const fyStartYear = (d: Date) => (d.getMonth() >= FY_START_MONTH ? d.getFullYear() : d.getFullYear() - 1);
/** Quarter start month within the FY (Apr, Jul, Oct, Jan). */
const quarterStartMonth = (d: Date) => {
  const offset = (d.getMonth() - FY_START_MONTH + 12) % 12;
  return (FY_START_MONTH + offset - (offset % 3)) % 12;
};
const quarterStart = (d: Date) => {
  const m = quarterStartMonth(d);
  const y = m > d.getMonth() ? d.getFullYear() - 1 : d.getFullYear();
  return date(y, m, 1);
};

/** Resolve a preset to a concrete range. `CustomDate` returns `fallback`. */
export const resolvePreset = (
  id: DatePresetId,
  fallback: DateRange,
  today: Date = new Date()
): DateRange => {
  const t = date(today.getFullYear(), today.getMonth(), today.getDate());
  const y = t.getFullYear();
  const m = t.getMonth();
  const fy = fyStartYear(t);
  const qs = quarterStart(t);

  switch (id) {
    case "Today":
      return { from: toYmd(t), to: toYmd(t) };
    case "ThisWeek": {
      const start = date(y, m, t.getDate() - t.getDay());
      return { from: toYmd(start), to: toYmd(date(start.getFullYear(), start.getMonth(), start.getDate() + 6)) };
    }
    case "ThisMonth":
      return { from: toYmd(date(y, m, 1)), to: toYmd(monthEnd(y, m)) };
    case "MonthToDate":
      return { from: toYmd(date(y, m, 1)), to: toYmd(t) };
    case "ThisQuarter":
      return { from: toYmd(qs), to: toYmd(monthEnd(qs.getFullYear(), qs.getMonth() + 2)) };
    case "QuarterToDate":
      return { from: toYmd(qs), to: toYmd(t) };
    case "ThisYear":
      return { from: toYmd(date(fy, FY_START_MONTH, 1)), to: toYmd(date(fy + 1, FY_START_MONTH, 0)) };
    case "YearToDate":
      return { from: toYmd(date(fy, FY_START_MONTH, 1)), to: toYmd(t) };
    case "PreviousDay": {
      const d = date(y, m, t.getDate() - 1);
      return { from: toYmd(d), to: toYmd(d) };
    }
    case "PreviousWeek": {
      const start = date(y, m, t.getDate() - t.getDay() - 7);
      return { from: toYmd(start), to: toYmd(date(start.getFullYear(), start.getMonth(), start.getDate() + 6)) };
    }
    case "PreviousMonth":
      return { from: toYmd(date(y, m - 1, 1)), to: toYmd(monthEnd(y, m - 1)) };
    case "PreviousQuarter": {
      const pq = date(qs.getFullYear(), qs.getMonth() - 3, 1);
      return { from: toYmd(pq), to: toYmd(monthEnd(pq.getFullYear(), pq.getMonth() + 2)) };
    }
    case "PreviousYear":
      return { from: toYmd(date(fy - 1, FY_START_MONTH, 1)), to: toYmd(date(fy, FY_START_MONTH, 0)) };
    case "CustomDate":
    default:
      return fallback;
  }
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "01 Apr 2026" */
export const formatYmd = (ymd: string): string => {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
};
